/**
 * The canonical card ↔ ordinal ↔ base-32 mapping — the JS half of the one
 * load-bearing cross-language serde contract (see docs/SERDE.md). Keyed on the
 * CardImage code string ("JH", "10S") the backend speaks. ordinal =
 * suitIndex * 8 + rankIndex, the card's index in a freshly-built deck. Frozen
 * and gated by the golden table both runtimes reproduce (card-ordinal.test.js).
 */

// Order MUST match Java's Suit / Rank enum ordinals — that identity is the contract.
const SUITS = ["D", "H", "S", "C"];
const RANKS = ["7", "8", "9", "10", "J", "Q", "K", "A"];
const RANKS_PER_SUIT = 8;
const CARD_COUNT = 32;

/** Wire suit code → the suit name every renderer speaks (`cards/cards.js`, `hints.js`'s inline
 *  card refs). Exported for the same reason `RANK_NAMES` is: a server-rendered fragment or a hint
 *  reason arg can carry the code alone, and this is where that mapping already lives. */
export const SUIT_NAMES = {
  D: "diamonds",
  H: "hearts",
  S: "spades",
  C: "clubs",
};

/** Wire rank code → the rank name every renderer speaks (`cards/cards.js#rankDisplay`,
 *  `POINT_VALUES`). Exported because a server-rendered fragment can carry the code alone —
 *  round-inspection.html's card chips do — and this is where that mapping already lives. */
export const RANK_NAMES = {
  7: "seven",
  8: "eight",
  9: "nine",
  10: "ten",
  J: "jack",
  Q: "queen",
  K: "king",
  A: "ace",
};

/** The ordinal 0–31 of a CardImage code like "JH" or "10S". */
export function ordinalOf(code) {
  const suitIndex = SUITS.indexOf(code.slice(-1));
  const rankIndex = RANKS.indexOf(code.slice(0, -1));
  if (suitIndex < 0 || rankIndex < 0) {
    throw new Error(`Invalid card code: ${code}`);
  }
  return suitIndex * RANKS_PER_SUIT + rankIndex;
}

/** The CardImage code for an ordinal 0–31. */
export function codeOf(ordinal) {
  checkOrdinal(ordinal);
  return (
    RANKS[ordinal % RANKS_PER_SUIT] +
    SUITS[Math.floor(ordinal / RANKS_PER_SUIT)]
  );
}

/** The one-character base-32 text form of an ordinal 0–31. */
export function base32Of(ordinal) {
  checkOrdinal(ordinal);
  return ordinal.toString(32);
}

/** The ordinal 0–31 a one-character base-32 text form denotes. */
export function ordinalFromBase32(text) {
  if (typeof text !== "string" || text.length !== 1) {
    throw new Error(`Invalid base-32 card char: ${text}`);
  }
  const ordinal = parseInt(text, 32);
  checkOrdinal(ordinal);
  return ordinal;
}

/** One card's {code, suit, rank} view, decoded from its wire ordinal. */
export function cardView(ordinal) {
  const code = codeOf(ordinal);
  return {
    code,
    suit: SUIT_NAMES[code.slice(-1)],
    rank: RANK_NAMES[code.slice(0, -1)],
  };
}

/** A base-32 card sequence (hand, deal order, skat, trick, ...) decoded to its
 *  card views, in wire order. */
export function decodeSequence(base32) {
  return [...base32].map((ch) => cardView(ordinalFromBase32(ch)));
}

function checkOrdinal(ordinal) {
  if (!Number.isInteger(ordinal) || ordinal < 0 || ordinal >= CARD_COUNT) {
    throw new Error(`Card ordinal out of range 0–31: ${ordinal}`);
  }
}
